<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user.store.ts'
import type { ProjectSettings, } from '@/components/projects/interfaces.ts'
import * as IProjects from '@/components/projects/interfaces.ts'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import {
  FindProjects_WidgetAssistantPhotos_PlatformLanguages,
} from '@/components/projects/graphql/project.queries.ts'
import { cloneDeep, isObject } from 'lodash'
import { storeToRefs } from 'pinia'
import { UpdateProjectSettingsMutation } from '@/components/projects/graphql/project.mutations.ts'
import { useToast } from 'vue-toastification'
import { useTranslations } from '@/composables/useTranslations.ts'
import { usePageTitle } from '@/composables/usePageTitle.ts'
import type { PlatformLanguage } from '@/components/interfaces/graphql-common.interfaces.ts'
import ProjectSettingsForm from '@/components/projects/ProjectSettingsForm.vue'
import { isNumeric } from '@/utils/util.ts'

const { t } = useTranslations()
const projectVariables = ref<{ input: { projectIds: number[] }; projectId: number }>({ input: { projectIds: [] }, projectId: -1 })

function setGqlQueryVariables(projectId: number): void {
  projectVariables.value = {
    input: { projectIds: [projectId] },
    projectId: projectId,
  }
}

const toast = useToast()
const currentProjectId = ref<number | null>(null)

// Queries & mutations
const {
  onDone: onSettingsUpdated,
  onError: onSettingsUpdateFailed,
  mutate: submitSettings,
} = useMutation<{
  settings: IProjects.ProjectSettings
}>(UpdateProjectSettingsMutation, { fetchPolicy: 'no-cache' })
const {
  onError: onSettingsLoadingFailed,
  load: loadProjectSettings,
  loading: isProjectSettingsLoading,
  result
} = useLazyQuery<{
  projects: IProjects.Project[]
  languages: PlatformLanguage[]
  widgetAssistantPhotos: IProjects.IWidgetAssistantPhoto[]
}>(FindProjects_WidgetAssistantPhotos_PlatformLanguages, { variables: projectVariables, fetchPolicy: 'cache-and-network' })

// @ts-expect-error Jevgeni fix it
onSettingsUpdated(({ data: { settings } }) => {
  console.log('onSettingsUpdated | settings:', settings)
  toast.success(t('settings.notifications.settingsSaved'))
})

onSettingsUpdateFailed((err) => {
  console.log('onSubmit | onError:', err)
  // isLoading.value = false
  toast.error(t('settings.notifications.saveError'))
})

onSettingsLoadingFailed((err) => {
  toast.error('Failed to load settings')
  console.error('onSettingsLoadingFailed', err)
})

const userStore = useUserStore()
const { projects, projectSelected, hasPresentationModeEnabled } = storeToRefs(userStore)
const route = useRoute()
const formRef = ref<{ synsFormWithLatestState: () => void } | null>(null)

onMounted(() => {
  const { stop: stopSelectedProjectWatcher } = watch<IProjects.Project | null>(
    projectSelected,
    (project) => {
      loadProjectIfChosen(project)
    },
  )

  // Watcher for changing route parameters
  const { stop: stopRouteWatcher } = watch(
    () => route.params.projectKey,
    (newProjectKey) => {
      const project = projects.value.find(({ key }) => key === newProjectKey) ?? null
      loadProjectIfChosen(project)
    },
  )

  onUnmounted(() => {
    stopSelectedProjectWatcher()
    stopRouteWatcher()
  })

  loadProjectIfChosen(projectSelected.value)
})

onBeforeRouteLeave((to, from, next) => {
  /* if (hasChanges.value) {
    const message = t('settings.confirm.unsavedChanges')
    const answer = window.confirm(message)
    next(answer ? undefined : false)
  } else {
    next()
  } */
  next()
})

function loadProjectIfChosen(project: IProjects.Project | null): void {
  if (project && typeof project.id === 'number' && project.id !== currentProjectId.value) {
    loadProjectById(project.id)
  }
}

async function loadProjectById(projectId: number): Promise<void> {
  setGqlQueryVariables(projectId)
  await loadProjectSettings()
}

async function onSubmit(values: ProjectSettings): Promise<void> {
  console.log('onSubmit | values', values)
  const formValues = cloneDeep(values)
  delete formValues.ai.chatGptAssistant // Exclude remote form

  await submitSettings({
    variables: {
      projectId: projectSelected.value!.id,
      input: formValues
    }
  })

  formRef.value?.synsFormWithLatestState()
}

function onInvalidSubmit(input: any): void {
  console.log('onInvalidSubmit:', input)
  toast.error(`${t('settings.notifications.formError')}${JSON.stringify(input.errors, null, '\t')})`)
}

const title = computed(() => {
  const projectName = hasPresentationModeEnabled.value ? '***' : (projectSelected?.value?.title || '')
  return projectName ? `${projectName} | ${t('navigation.settings')}` : t('navigation.settings')
})
usePageTitle(title)

// The query `result` is a union with partial/streaming states; the form needs the complete shape
const loadedSettings = computed(() => result.value as {
  projects: IProjects.Project[]
  languages: PlatformLanguage[]
  widgetAssistantPhotos: IProjects.IWidgetAssistantPhoto[]
} | null | undefined)
</script>
<template>
  <div v-if="!isProjectSettingsLoading && isObject(loadedSettings?.projects[0]?.settings) && isNumeric(projectSelected?.id)">
    <ProjectSettingsForm
      @submit="onSubmit"
      @invalid-submit="onInvalidSubmit"
      :project-id="projectSelected!.id as number"
      :project-key="projectSelected!.key"
      :settings="cloneDeep(loadedSettings?.projects[0]?.settings)"
      :languages="loadedSettings?.languages ?? []"
      :assistant-photos="loadedSettings?.widgetAssistantPhotos ?? []"
      ref="formRef"
    />
  </div>
  <div class="pre-footer-text">{{ t('project.settingsOverviewDescription') }}</div>
</template>
